import { idParam, ROLES } from "../../../helpers/commons.helpers";
import store from "../../../store/store";
import { BREAD_EDIT_QUOTE, BREAD_EDIT_RESERVATION, BREAD_NEW_QUOTE, BREAD_NEW_RESERVATION, BREAD_RESERVATIONS } from "../constants/reservation.constants";

/**
 * Reservation route definitions.
 *
 * These routes are lazy-loaded and intended to be used as children of the
 * authenticated layout. Each route contains a `meta` object used by navigation
 * guards and permission checks and some routes include a `beforeEnter` guard
 * that sets the purchase type in the store depending on query parameters.
 *
 * @typedef {Object} ReservationRouteMeta
 * @property {boolean} requiresAuth - When true the route requires authentication.
 * @property {Array<string>} roles - Array of role identifiers allowed to access the route.
 *
 * @typedef {import('vue-router').NavigationGuard} NavigationGuard
 *
 * @type {Array<import('vue-router').RouteConfig>}
 */
export default [
    {
        path: "/reservations",
        name: "Reservations",
        meta: {
            title: "Reservations",
            requiresAuth: true,
            roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.SUPPORT],
            stage: "RESERVATION",
            bread: BREAD_RESERVATIONS
        },
        component: () => import(/* webpackChunkName: "reservations" */ "@/modules/reservations/pages/Reservations.vue")
    },
    {
        path: "/reservations/new",
        name: "NewReservation",
        meta: {
            title: "New Reservation",
            requiresAuth: true,
            roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.SUPPORT],
            stage: "RESERVATION",
            bread: BREAD_NEW_RESERVATION
        },
        beforeEnter: (to, from, next) => {
            let { productId } = to.query;
            store.commit("setPurchaseType", productId ? "EXPERIENCE_RESERVATION" : "TRANSFER_RESERVATION");
            next();
        },
        component: () => import(/* webpackChunkName: "reservations" */ "@/modules/reservations/pages/wrapper/NewReservation.vue")
    },
    {
        path: `/reservations/${idParam()}`,
        name: "Reservation",
        meta: {
            title: "Reservation",
            requiresAuth: true,
            roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.SUPPORT],
            stage: "RESERVATION",
            bread: BREAD_EDIT_RESERVATION
        },
        beforeEnter: (to, from, next) => {
            let { productId } = to.query;
            store.commit("setPurchaseType", productId ? "EXPERIENCE_RESERVATION" : "TRANSFER_RESERVATION");
            next();
        },
        component: () => import(/* webpackChunkName: "reservations" */ "@/modules/reservations/pages/wrapper/EditReservation.vue")
    },
    {
        path: "/reservations/new-quote",
        name: "NewQuote",
        meta: {
            title: "New Quote",
            requiresAuth: true,
            roles: [
                ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.SUPPORT,
            ],
            stage: "QUOTE",
            bread: BREAD_NEW_QUOTE
        },
        beforeEnter: (to, from, next) => {
            let { productId } = to.query;
            store.commit("setPurchaseType", productId ? "EXPERIENCE_RESERVATION" : "TRANSFER_RESERVATION");
            next();
        },
        component: () => import(/* webpackChunkName: "reservations" */ "@/modules/reservations/pages/wrapper/NewReservation.vue")
    },
    {
        path: `/reservations/quote/${idParam()}`,
        name: "Quote",
        meta: {
            title: "Quote",
            requiresAuth: true,
            roles: [
                ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.SUPPORT
            ],
            stage: "QUOTE",
            bread: BREAD_EDIT_QUOTE
        },
        beforeEnter: (to, from, next) => {
            let { productId } = to.query;
            store.commit("setPurchaseType", productId ? "EXPERIENCE_RESERVATION" : "TRANSFER_RESERVATION");
            next();
        },
        component: () => import(/* webpackChunkName: "reservations" */ "@/modules/reservations/pages/wrapper/EditReservation.vue")
    },
]